import React from 'react';
import { Badge, Form, Input, Tabs, Tooltip } from 'antd';
import { useTranslation } from '../../shared/i18n';

/**
 * Per-language catalog text (DB column `i18n`, see api/app/service/i18n/CatalogI18n.php) inside an antd Form.
 *
 *   <I18nFieldTabs fields={[{ key: 'name', label: '名称' }, { key: 'description', label: '描述', type: 'textarea' }]}
 *       base={<>{the form's own name / description items}</>} />
 *
 * - Tab 简体中文 shows `base` (the row's own columns); the other tabs edit form values i18n.<lang>.<field>.
 * - Empty = falls back to the 简体中文 text for customers using that language.
 * - Set initial values with parseI18n(row.i18n); submit cleanI18n(mergeI18n(row.i18n, values.i18n)).
 * - A field may give render(lang) instead of a type for its own inputs (e.g. the spec-label table).
 * - All tabs stay mounted (forceRender) so every language is submitted.
 */
export const I18N_LANGS = [
    { code: 'en', label: 'English' },
    { code: 'zh-TW', label: '繁體中文' },
    { code: 'ja', label: '日本語' },
    { code: 'ko', label: '한국어' },
    { code: 'ru', label: 'Русский' },
    { code: 'es', label: 'Español' },
    { code: 'ms', label: 'Bahasa Melayu' },
];

/** Row value (object or JSON string) → { lang: { field: value } }. */
export const parseI18n = (v) => {
    let o = v;
    if (typeof o === 'string') {
        try { o = JSON.parse(o); } catch (_) { o = null; }
    }
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
};

const isObj = (x) => !!x && typeof x === 'object' && !Array.isArray(x);
const filled = (x) => {
    if (Array.isArray(x)) return x.some((s) => String(s ?? '').trim() !== '');
    if (isObj(x)) return Object.values(x).some(filled);
    return String(x ?? '').trim() !== '';
};

/** One value cleaned: strings trimmed, lists without blanks, nested maps (feature_schema labels) without empty keys. */
const cleanValue = (x) => {
    if (Array.isArray(x)) {
        const items = x.map((s) => String(s ?? '').trim()).filter(Boolean);
        return items.length ? items : undefined;
    }
    if (isObj(x)) {
        const o = {};
        Object.entries(x).forEach(([k, y]) => {
            const c = cleanValue(y);
            if (c !== undefined) o[k] = c;
        });
        return Object.keys(o).length ? o : undefined;
    }
    return filled(x) ? String(x).trim() : undefined;
};

/** Form value → what the API stores: empty fields / languages dropped, features trimmed; null when nothing left. */
export const cleanI18n = (v) => {
    const out = {};
    Object.entries(parseI18n(v)).forEach(([lang, fields]) => {
        if (!isObj(fields)) return;
        const row = cleanValue(fields);
        if (row) out[lang] = row;
    });
    return Object.keys(out).length ? out : null;
};

/**
 * The row's stored i18n with the form's values laid over it (form wins, empty strings included). The form only
 * returns values of mounted fields, so this keeps translations this dialog does not show (other fields/languages).
 */
export const mergeI18n = (stored, formValue) => {
    const merge = (a, b) => {
        if (!isObj(b)) return b === undefined ? a : b;
        const o = isObj(a) ? { ...a } : {};
        Object.entries(b).forEach(([k, y]) => { o[k] = merge(o[k], y); });
        return o;
    };
    return merge(parseI18n(stored), parseI18n(formValue));
};

const lines = {
    getValueProps: (v) => ({ value: Array.isArray(v) ? v.join('\n') : (v ?? '') }),
    getValueFromEvent: (e) => String(e?.target?.value ?? '').split('\n'),
};

const I18nFieldTabs = ({ fields, base, name = 'i18n', baseLabel = '简体中文' }) => {
    const { t } = useTranslation();
    const form = Form.useFormInstance();
    const values = parseI18n(Form.useWatch(name, form));
    const has = (lang) => Object.values(values[lang] || {}).some(filled);

    const items = [
        { key: 'base', label: baseLabel, forceRender: true, children: base },
        ...I18N_LANGS.map((l) => ({
            key: l.code,
            forceRender: true,
            label: has(l.code) ? <Badge dot offset={[4, 0]}>{l.label}</Badge> : l.label,
            children: fields.map((f) => {
                if (f.render) return <React.Fragment key={f.key}>{f.render(l.code)}</React.Fragment>;
                const common = { key: f.key, name: [name, l.code, f.key], label: f.label };
                if (f.type === 'list') {
                    return (
                        <Form.Item {...common} {...lines} tooltip={t('One item per line')}>
                            <Input.TextArea rows={f.rows || 4} />
                        </Form.Item>
                    );
                }
                return (
                    <Form.Item {...common}>
                        {f.type === 'textarea' ? <Input.TextArea rows={f.rows || 3} /> : <Input />}
                    </Form.Item>
                );
            }),
        })),
    ];
    return (
        <Tabs
            size="small"
            items={items}
            tabBarExtraContent={<Tooltip title={t('Leave empty to show the Simplified Chinese text')}><span style={{ color: '#999' }}>?</span></Tooltip>}
        />
    );
};

export default I18nFieldTabs;
